import { Button, Input, message, Modal } from "antd";
import type { GeoPoint, Port, QueryLicenseResponse, Ship, ShipHistoryForm, ShipInfoVo, TrackInfo, VehicleHistoryVo, VehicleInfoResponse } from "@zhihaoscm/entity";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { shipService, type VehicleQueryLimitInfo } from "@/api/shipService";
import { portServcie } from "@/api/portService";
import { isChainCloudTenant } from "@/api/legacy";
import { shipFollowCustomService } from "@/api/shipFollowCustomService";
import { vehicleService } from "@/api/vehicleService";
import { ZhihaoMap } from "@/components/media";
import type { ZhihaoMapInstance } from "@/components/media/ZhihaoMap";
import { FindShipAndPortResultList, FindShipDetail, FindShipMixedHistory, FindShipToolPanel } from "./FindLocationComponents";
import { FindRoutePlanningPanel, FindShipFollowPanel, type RoutePlanningResult } from "./FindLocationPanels";
import { TruckSearchDetail, TruckSearchHistory, TruckTrajectoryMini, TruckTrajectoryPanel, type TruckTrajectoryEntry } from "./TruckLocationComponents";
import { FindHarborCard, FindMeasurePanel, FindPortActiveCard, getPortCoordinates, renderAndHighlightPort, type MeasureLine } from "./FindPortComponents";
import { FindShipCustomPanel } from "./FindShipCustomPanel";
import { FindShipTrackPanel, type ShipTrackEntry } from "./FindShipTrackPanel";
import "./location-pages.css";

/** 旧 Vue `MAP_ZOOM.shipGreenDotMax`：达到该层级后绿点图层按钮不可操作。 */
const SHIP_GREEN_DOT_MAX_ZOOM = 11;

/** Vue 在渲染普通查船结果前会关闭三类设备标记，避免船名后出现电话、摄像头和水尺图标。 */
export const normalizeFindLocationMapShip = (ship: ShipInfoVo): ShipInfoVo => ({
    ...ship,
    existMobile: 0,
    existDevice: 0,
    existWaterGauge: 0
});

/** 读取 Vue Pinia 地图 store 使用的同名 localStorage，页面迁移后继续沿用用户上次视野。 */
const readLegacyMapState = (key: string, defaultZoom: number) => {
    try {
        const stored = JSON.parse(localStorage.getItem(key) || "{}") as { zoom?: number; center?: [number, number] };
        return { zoom: Number(stored.zoom) || defaultZoom, center: Array.isArray(stored.center) ? stored.center : [114.84, 30.52] as [number, number] };
    } catch {
        return { zoom: defaultZoom, center: [114.84, 30.52] as [number, number] };
    }
};

/** Vue 在离开和刷新页面时写回 zoom/center；React 复用同一 key，避免切页后视野跳回默认值。 */
const saveLegacyMapState = (key: string, map?: ZhihaoMapInstance) => {
    const zoom = map?.getZoom?.();
    const center = map?.getCenter?.();
    if (zoom && center) localStorage.setItem(key, JSON.stringify({ zoom, center }));
};

/** 查船页覆盖关键字搜索、历史、船舶详情和 AIS 轨迹查询。 */
export function FindLocationPage() {
    const pageRef = useRef<HTMLElement>(null);
    const mapRef = useRef<ZhihaoMapInstance | undefined>(undefined);
    const initialMapState = useRef(readLegacyMapState("zhihao-web-map", 13));
    const initialZoom = initialMapState.current.zoom < 10 ? 13 : initialMapState.current.zoom;
    const [params] = useSearchParams();
    const [keyword, setKeyword] = useState(params.get("mmsi") ?? "");
    const [ships, setShips] = useState<Ship[]>([]);
    const [ports, setPorts] = useState<Port[]>([]);
    const [history, setHistory] = useState<ShipHistoryForm[]>([]);
    const [selected, setSelected] = useState<ShipInfoVo>();
    const [mapShips, setMapShips] = useState<ShipInfoVo[]>([]);
    const [loading, setLoading] = useState(false);
    const [panel, setPanel] = useState<"hidden" | "history" | "result">("hidden");
    const [showGreenLayer, setShowGreenLayer] = useState(true);
    const [currentZoom, setCurrentZoom] = useState(initialZoom);
    const [layerType, setLayerType] = useState<"satellite" | "vector">("satellite");
    const [fullscreen, setFullscreen] = useState(false);
    const [fallbackFullscreen, setFallbackFullscreen] = useState(false);
    const [showRoutePlanning, setShowRoutePlanning] = useState(false);
    const [showFollowPanel, setShowFollowPanel] = useState(false);
    const [shipUsage, setShipUsage] = useState<VehicleQueryLimitInfo | null>(null);
    const [showShipUsage, setShowShipUsage] = useState(false);
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const [selectedPort, setSelectedPort] = useState<Port>();
    const [activePort, setActivePort] = useState<Port>();
    const [showHarbors, setShowHarbors] = useState(false);
    const [measureOpen, setMeasureOpen] = useState(false);
    const [measureLines, setMeasureLines] = useState<MeasureLine[]>([]);
    const [measureUnit, setMeasureUnit] = useState("nm");
    const [searchMode, setSearchMode] = useState<"ship" | "custom">("ship");
    const [customArea, setCustomArea] = useState<GeoPoint[]>([]);
    const [customShips, setCustomShips] = useState<ShipInfoVo[]>([]);
    const [customRefreshTime, setCustomRefreshTime] = useState("");
    const [trackEntries, setTrackEntries] = useState<ShipTrackEntry[]>([]);
    const [activeTrackId, setActiveTrackId] = useState("");
    const [trackMini, setTrackMini] = useState(false);
    const greenBeforePlanningRef = useRef<boolean | null>(null);
    const harborBeforePlanningRef = useRef<boolean | null>(null);
    useEffect(() => {
        void shipService.getHistoryList().then(items => setHistory(items as ShipHistoryForm[]));
        if (isChainCloudTenant()) void shipService.findLimitNum({ type: 1 }).then(setShipUsage);
        const persist = () => saveLegacyMapState("zhihao-web-map", mapRef.current);
        window.addEventListener("beforeunload", persist);
        return () => { window.removeEventListener("beforeunload", persist); persist(); };
    }, []);
    /** 完全沿用旧页的视野四角参数顺序，由 zhihao-ui 提供范围。 */
    const loadViewportShips = async ([left, top, right, bottom]: [number, number, number, number]) => {
        try {
            setMapShips(
                (await shipService.extent({
                    leftTopPoint: { lng: left, lat: top },
                    rightTopPoint: { lng: right, lat: top },
                    rightBottomPoint: { lng: right, lat: bottom },
                    leftBottomPoint: { lng: left, lat: bottom },
                    time: Date.now() - 60_000
                })).map(normalizeFindLocationMapShip)
            );
        } catch {
            // 保留上一次成功数据，行为与旧页一致。
        }
    };
    const search = async () => {
        if (!keyword.trim()) return;
        setLoading(true);
        try {
            const [shipList, portPage] = await Promise.all([
                shipService.search(keyword.trim()),
                portServcie.pagingSelector({ portName: keyword.trim() })
            ]);
            setShips(shipList || []);
            setPorts(portPage?.list || []);
            setPanel("result");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (params.get("mmsi")) void search();
    }, []);
    const queryTrackEntry = async (entry: ShipTrackEntry) => {
        const points = await shipService.aisTrack({ mmsi: entry.ship.id, start: entry.range[0].valueOf(), end: entry.range[1].valueOf() });
        if (!points.length) {
            // Vue 空轨迹不调用地图 render，也不进入可播放状态。
            setTrackEntries(items => items.length
                ? items.map(item => item.ship.id === entry.ship.id ? { ...item, points: [], length: undefined } : item)
                : [{ ...entry, points: [], length: undefined }]);
            void message.error("找不到轨迹数据，请查询其他时间范围");
            return;
        }
        mapRef.current?.track?.render(entry.ship.id, points, entry.color);
        const length = mapRef.current?.track?.getLength?.(entry.ship.id)?.length;
        // 首次打开时接口可能在 React 批处理提交前返回；空队列时补回当前 tab，避免快速响应吞掉面板。
        setTrackEntries(items => items.length
            ? items.map(item => item.ship.id === entry.ship.id ? { ...item, points, length } : item)
            : [{ ...entry, points, length }]);
        setShowGreenLayer(false);
    };
    /** 每艘船只创建一个轨迹 tab，首次打开默认查询最近一天。 */
    const openTrack = () => {
        if (!selected) return;
        const now = dayjs();
        const range: [dayjs.Dayjs, dayjs.Dayjs] = [now.subtract(1, "day"), now];
        const existing = trackEntries.find(item => item.ship.id === selected.id);
        let entry: ShipTrackEntry;
        if (!existing) {
            const colors = ["#ff0000", "#00ff00", "#0079ff", "#ffa500", "#800080", "#008080", "#ffc0cb", "#800000", "#000080", "#808000"];
            entry = { ship: selected, range, speed: 1, points: [], color: colors[trackEntries.length % colors.length], activeDays: 1 };
            setTrackEntries(items => [...items, entry!]);
        } else {
            // Vue 每次从详情点击“查询轨迹”都会重置为最近一天，而不是沿用上次手工范围。
            entry = { ...existing, ship: selected, range, activeDays: 1 };
            setTrackEntries(items => items.map(item => item.ship.id === selected.id ? entry : item));
        }
        setActiveTrackId(selected.id); setTrackMini(false); mapRef.current?.track?.remove?.(); mapRef.current?.ship.clear();
        // 面板先按旧页同步展开，轨迹数据随后回填，避免接口响应批处理吞掉首次 tab。
        void queryTrackEntry(entry);
    };
    /** 切换 tab 后恢复该船保存的范围并立即重新查询，与 Vue trackTabClick 一致。 */
    const activateTrack = (id: string) => {
        const entry = trackEntries.find(item => item.ship.id === id);
        if (!entry) return;
        setActiveTrackId(id);
        setSelected(entry.ship);
        void queryTrackEntry(entry);
    };
    /** 删除当前 tab 时选择相邻船舶并刷新轨迹，防止标题切了但地图仍停留在旧船。 */
    const removeTrack = (id: string) => {
        const index = trackEntries.findIndex(item => item.ship.id === id);
        const remaining = trackEntries.filter(item => item.ship.id !== id);
        mapRef.current?.track?.close?.(id);
        setTrackEntries(remaining);
        if (activeTrackId !== id) return;
        const next = remaining[Math.max(0, index - 1)] || remaining[0];
        setActiveTrackId(next?.ship.id || "");
        if (next) { setSelected(next.ship); void queryTrackEntry(next); }
    };
    /** 快捷日期是每个轨迹 tab 的独立状态，点击后和 Vue 一样立即发起查询。 */
    const selectTrackDays = (days: 1 | 7 | 30) => {
        const entry = trackEntries.find(item => item.ship.id === activeTrackId);
        if (!entry) return;
        const now = dayjs();
        const next: ShipTrackEntry = { ...entry, range: [now.subtract(days, "day"), now], activeDays: days };
        setTrackEntries(items => items.map(item => item.ship.id === activeTrackId ? next : item));
        void queryTrackEntry(next);
    };
    /** 最小化会关闭地图轨迹；重新展开必须像 Vue maxTrack 一样重新渲染当前轨迹。 */
    const toggleTrackMini = () => {
        if (!trackMini) {
            setTrackMini(true);
            mapRef.current?.track?.close?.();
            return;
        }
        setTrackMini(false);
        const entry = trackEntries.find(item => item.ship.id === activeTrackId);
        if (entry) void queryTrackEntry(entry);
    };
    /** 与 Vue 一致，列表点击后再请求完整船舶档案。 */
    const selectShip = async (ship: Pick<Ship, "id">) => {
        if (isChainCloudTenant()) {
            const usage = await shipService.findLimitNum({ type: 1 });
            setShipUsage(usage);
            if (usage.overFlag === 1 || (usage.hasUnLimit !== 1 && usage.remainingTimes <= 0)) {
                setShowLimitDialog(true);
                return;
            }
        }
        const detail = normalizeFindLocationMapShip(await shipService.findCreate(ship.id));
        setShowShipUsage(false);
        if (isChainCloudTenant()) setShipUsage(await shipService.findLimitNum({ type: 1 }));
        // Vue 在详情返回后先校验 AIS 坐标；无坐标时不打开空白详情卡。
        if (!detail.lat || !detail.lon) {
            void message.error("当前船舶AIS信息未更新");
            return;
        }
        setSelected(detail);
        setSelectedPort(undefined);
        setActivePort(undefined);
        setPanel("hidden");
        mapRef.current?.wharf?.removeWharfLayer?.();
        mapRef.current?.port?.selected?.();
        mapRef.current?.port?.clearRed?.();
        if ((mapRef.current?.getZoom?.() ?? 0) < 13) mapRef.current?.setZoom?.(13);
        mapRef.current?.setCenter?.(detail.lon, detail.lat);
        window.setTimeout(() => void mapRef.current?.ship.selected(detail), 100);
        // 旧页不阻塞详情展示，历史写入失败也不影响当前查询结果。
        void shipService.addHistory({ id: detail.id, name: detail.name, cnname: detail.cnname, type: Number(detail.type) });
    };
    /** 港口历史和搜索结果都先按旧接口补全详情，再写入同一搜索历史。 */
    const selectPort = async (port: Pick<Port, "id"> & Partial<Port>) => {
        const detail = await portServcie.get(port.id);
        if (!detail) return;
        setSelected(undefined);
        setPanel("hidden");
        setSelectedPort(detail);
        setActivePort(detail);
        mapRef.current?.wharf?.removeWharfLayer?.();
        mapRef.current?.port?.selected?.();
        renderAndHighlightPort(mapRef.current, detail);
        await shipService.addHistory({
            id: String(detail.id),
            shortName: detail.shortName,
            type: detail.type,
            provinceCode: detail.provinceCode,
            cityCode: detail.cityCode,
            regionCode: detail.regionCode
        });
    };
    /** 地图直接点港口只展示右上信息卡，不切换左侧搜索卡。 */
    const selectPortFromMap = async (raw: unknown) => {
        const id = Number((raw as { id?: number })?.id);
        if (!id) return;
        const detail = await portServcie.get(id);
        if (!detail) return;
        setActivePort(detail);
        renderAndHighlightPort(mapRef.current, detail);
    };
    const centerPort = (port: Port) => {
        const coordinates = getPortCoordinates(port);
        if (!coordinates.length) return;
        mapRef.current?.setCenter?.(coordinates[0][0], coordinates[0][1]);
        mapRef.current?.setZoom?.(13);
    };
    const toggleMeasure = () => {
        setMeasureOpen(value => {
            if (value) mapRef.current?.drawLine?.close?.();
            else { mapRef.current?.drawLine?.open?.(); mapRef.current?.drawLine?.setUnit?.(measureUnit); }
            return !value;
        });
    };
    const toggleHarbors = async () => {
        const next = !showHarbors;
        setShowHarbors(next);
        if (!next) { mapRef.current?.port?.clear?.(); return; }
        const list = await portServcie.getCustomPortSelector({ type: 2 });
        mapRef.current?.port?.clearRed?.();
        mapRef.current?.port?.render?.(list, (mapRef.current?.getZoom?.() ?? 0) >= 11);
    };
    /** 自定义区域完成后使用接口要求的经纬度对象查询，并以一分钟前作为 AIS 时间边界。 */
    const loadCustomShips = async (area = customArea) => {
        if (!area.length) return;
        const list = await shipService.getShipCustom({ geoPointList: area, time: Date.now() - 60_000 });
        setCustomShips(list);
        setMapShips(list.map(normalizeFindLocationMapShip));
        setCustomRefreshTime(dayjs().format("YYYY-MM-DD HH:mm:ss"));
    };
    const switchSearchMode = (mode: "ship" | "custom") => {
        if (mode === searchMode) return;
        setSearchMode(mode);
        if (mode === "custom") {
            setSelected(undefined); setSelectedPort(undefined); setActivePort(undefined); setPanel("hidden"); setKeyword("");
            if (measureOpen) { mapRef.current?.drawLine?.close?.(); setMeasureOpen(false); setMeasureLines([]); }
            mapRef.current?.drawPolygon?.open?.();
        } else mapRef.current?.drawPolygon?.close?.();
    };
    /** 历史删除成功后同步移除当前条目。 */
    const deleteHistory = async (item: ShipHistoryForm) => {
        await shipService.deleteHistory(item);
        setHistory(items => items.filter(row => row.id !== item.id));
    };
    useEffect(() => {
        /** Esc 退出原生全屏时，必须把按钮图标同步回未全屏状态。 */
        const syncFullscreen = () => setFullscreen(document.fullscreenElement === pageRef.current || fallbackFullscreen);
        document.addEventListener("fullscreenchange", syncFullscreen);
        return () => document.removeEventListener("fullscreenchange", syncFullscreen);
    }, [fallbackFullscreen]);
    /** Vue 对查船页面容器全屏；浏览器拒绝时退化为视口级全屏，保证点击必有结果。 */
    const switchFullscreen = async () => {
        if (fallbackFullscreen) { setFallbackFullscreen(false); setFullscreen(false); return; }
        if (document.fullscreenElement === pageRef.current) { await document.exitFullscreen?.(); return; }
        try {
            if (!pageRef.current?.requestFullscreen) throw new Error("Fullscreen API unavailable");
            await pageRef.current.requestFullscreen();
            setFullscreen(true);
        } catch {
            setFallbackFullscreen(true);
            setFullscreen(true);
        }
    };
    /** 关注接口完成后仅更新当前详情的关注标记。 */
    const followShip = async () => {
        if (!selected) return;
        await shipFollowCustomService.follow(selected.id);
        setSelected({ ...selected, isFollowed: selected.isFollowed ? 0 : 1 });
    };
    /** Vue 仅在输入框聚焦时打开面板：空值显示历史，有值恢复搜索结果。 */
    const openSearchPanel = () => {
        setSelected(undefined);
        setPanel(keyword ? "result" : "history");
    };
    /** 清空输入框后恢复历史面板，与 Vue 的 clearSearchValue 一致。 */
    const clearSearch = () => {
        setKeyword("");
        setShips([]);
        setPorts([]);
        setSelected(undefined);
        setPanel("history");
    };
    /** 从关注列表进入船舶详情，关注浮层按 Vue 行为保持打开。 */
    const selectFollowedShip = async (shipId: string) => {
        // Vue 复用完整 selectShip 流程（含会员次数校验），且关注列表保持打开。
        await selectShip({ id: shipId });
    };
    /** 规划完成后将接口轨迹交给旧地图 runtime 绘制，并关闭绿点层。 */
    const applyRoutePlanning = (result: RoutePlanningResult) => {
        setShowGreenLayer(false);
        setShowHarbors(false);
        const routePorts = [result.origin, result.destination].map((port, index) => ({
            ...port,
            id: port.id || 1000 + index,
            name: port.shortName || port.name || `港口${index + 1}`,
            shortName: port.shortName || port.name || `港口${index + 1}`,
            remark: `航线规划${port.type || "码头"}`,
            fullAddress: port.shortName || port.name || `港口${index + 1}`
        }));
        // 旧地图负责航线、红色起终点和船层；React 不重新绘制这些地图元素。
        mapRef.current?.track?.close?.();
        mapRef.current?.track?.render("route-planning", result.route, "red", { showTime: false, lineWidth: 4, showPopup: false });
        mapRef.current?.ship.clear();
        mapRef.current?.port?.clear?.();
        mapRef.current?.port?.clearRed?.();
        mapRef.current?.port?.render?.(routePorts, true);
        mapRef.current?.port?.setRed?.(routePorts[0]);
        mapRef.current?.port?.setRed?.(routePorts[1]);
    };
    /** 关闭规划面板时清除规划轨迹并恢复进入规划前的绿点层。 */
    const closeRoutePlanning = () => {
        setShowRoutePlanning(false);
        mapRef.current?.track?.remove?.();
        mapRef.current?.port?.clear?.();
        mapRef.current?.port?.clearRed?.();
        const restoreHarbors = harborBeforePlanningRef.current ?? false;
        setShowHarbors(restoreHarbors);
        if (restoreHarbors) {
            void portServcie.getCustomPortSelector({ type: 2 }).then(list => {
                mapRef.current?.port?.render?.(list, (mapRef.current?.getZoom?.() ?? 0) >= 11);
            });
        }
        if (greenBeforePlanningRef.current !== null) setShowGreenLayer(greenBeforePlanningRef.current);
        greenBeforePlanningRef.current = null;
        harborBeforePlanningRef.current = null;
    };
    return (
        <section ref={pageRef} className={`find-location-page${fallbackFullscreen ? " is-fallback-fullscreen" : ""}`}>
            <ZhihaoMap
                label="船舶定位"
                layerType={layerType}
                center={initialMapState.current.center}
                showCenterMarker={false}
                zoom={initialZoom}
                showGreenLayer={showGreenLayer}
                ships={mapShips}
                selectedShipInfo={selected}
                onSelectShip={shipId => void selectShip({ id: shipId })}
                onSelectPort={port => void selectPortFromMap(port)}
                onSelectWharf={port => void selectPortFromMap(port)}
                onLineDrawEnd={lines => setMeasureLines(Array.isArray(lines) ? lines as MeasureLine[] : [])}
                onAreaDrawEnd={area => {
                    // ZhMap 返回 OpenLayers 坐标数组，旧 Vue 会先转换为接口 GeoPoint 结构。
                    const geoPoints = area.map(point => ({ lng: Number(point[0]), lat: Number(point[1]) }));
                    setCustomArea(geoPoints);
                    void loadCustomShips(geoPoints);
                }}
                onReady={instance => { mapRef.current = instance; instance.drawLine?.setUnit?.("nm"); }}
                onZoomChange={setCurrentZoom}
                onViewportChange={extent => void loadViewportShips(extent)}
            />
            <div className="find-location-search">
                <div className="find-location-tabs">
                    <button className={searchMode === "ship" ? "active" : ""} type="button" onClick={() => switchSearchMode("ship")}>
                        查船
                    </button>
                    <button className={searchMode === "custom" ? "active" : ""} type="button" onClick={() => switchSearchMode("custom")}>自定义搜船</button>
                </div>
                <div className="find-location-input">
                    <Input
                        allowClear
                        disabled={searchMode === "custom"}
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                        onClear={clearSearch}
                        onPressEnter={() => void search()}
                        onFocus={openSearchPanel}
                        placeholder={searchMode === "custom" ? "" : "请输入船舶或港口"}
                    />
                    <Button type={searchMode === "custom" ? "default" : "primary"} disabled={searchMode === "custom"} loading={loading} onClick={() => void search()}>
                        <i className="iconfont icon-a-search1" />
                        <span>搜索</span>
                    </Button>
                </div>
                {!selected && panel !== "hidden" ? (panel === "result" ? <FindShipAndPortResultList ships={ships} ports={ports} onSelectShip={ship => void selectShip(ship)} onSelectPort={port => void selectPort(port)} /> : <FindShipMixedHistory history={history} usage={isChainCloudTenant() ? shipUsage : null} showUsage={showShipUsage} onClear={() => void shipService.clearHistory().then(() => setHistory([]))} onDelete={item => void deleteHistory(item)} onSelectShip={item => void selectShip(item as Ship)} onSelectPort={item => void selectPort(item as unknown as Port)} onToggleUsage={() => setShowShipUsage(value => !value)} onUpgrade={() => window.open(`${window.location.origin}/console/me/member`)} />) : null}
                {selected ? <FindShipDetail ship={selected} fromList={panel === "result"} onBack={() => setSelected(undefined)} onClose={() => setSelected(undefined)} onTrack={() => void openTrack()} onFollow={() => void followShip()} /> : null}
            </div>
            <div className="find-location-tools">
                <button className="route-planning-btn" aria-expanded={showRoutePlanning} type="button" onClick={() => { if (!showRoutePlanning) { greenBeforePlanningRef.current = showGreenLayer; harborBeforePlanningRef.current = showHarbors; } setShowRoutePlanning(true); setShowFollowPanel(false); }}><span className="route-planning-icon" /><span>航线规划</span><i className="iconfont icon-Frame-110" /></button>
                <button className="follow-btn" aria-expanded={showFollowPanel} type="button" onClick={() => { if (showRoutePlanning) closeRoutePlanning(); setShowFollowPanel(true); }}><i className={`iconfont icon-a-aixin1${showFollowPanel ? " active" : ""}`} />关注的船</button>
                <button className="fullscreen-btn" aria-label={fullscreen ? "退出全屏" : "全屏"} type="button" onClick={() => void switchFullscreen()}><i className={`iconfont ${fullscreen ? "icon-quxiaoquanping" : "icon-quanping"}`} /></button>
            </div>
            {showFollowPanel ? <FindShipFollowPanel onClose={() => setShowFollowPanel(false)} onSelect={shipId => void selectFollowedShip(shipId)} onRemoved={shipId => setSelected(current => current?.id === shipId ? { ...current, isFollowed: 0 } : current)} /> : null}
            {showRoutePlanning ? <FindRoutePlanningPanel onClose={closeRoutePlanning} onPlanned={applyRoutePlanning} /> : null}
            {searchMode === "custom" ? <FindShipCustomPanel ships={customShips} hasArea={customArea.length > 0} refreshTime={customRefreshTime} onClose={() => switchSearchMode("ship")} onResetArea={() => { setCustomArea([]); setCustomShips([]); setCustomRefreshTime(""); mapRef.current?.drawPolygon?.reset?.(); }} onRefresh={() => void loadCustomShips()} /> : null}
            {trackEntries.length ? <FindShipTrackPanel entries={trackEntries} activeId={activeTrackId} mini={trackMini} onActive={activateTrack} onRemove={removeTrack} onRange={range => setTrackEntries(items => items.map(item => item.ship.id === activeTrackId ? { ...item, range } : item))} onSpeed={speed => setTrackEntries(items => items.map(item => item.ship.id === activeTrackId ? { ...item, speed } : item))} onDays={selectTrackDays} onQuery={() => { const entry = trackEntries.find(item => item.ship.id === activeTrackId); if (entry) void queryTrackEntry(entry); }} onPlay={() => { const entry = trackEntries.find(item => item.ship.id === activeTrackId); if (entry) mapRef.current?.track?.play?.(entry.ship.id, entry.speed); }} onCenter={() => mapRef.current?.track?.setCenter?.(activeTrackId)} onMini={toggleTrackMini} onClose={() => { mapRef.current?.track?.close?.(); setTrackEntries([]); setActiveTrackId(""); setTrackMini(false); }} /> : null}
            {selectedPort ? <FindHarborCard port={selectedPort} onClose={() => { setSelectedPort(undefined); mapRef.current?.wharf?.removeWharfLayer?.(); }} onCenter={() => centerPort(selectedPort)} onSelectChild={port => void selectPort(port)} /> : null}
            {activePort ? <FindPortActiveCard port={activePort} onCenter={() => centerPort(activePort)} onClose={() => { setActivePort(undefined); mapRef.current?.port?.selected?.(); mapRef.current?.port?.clearRed?.(); mapRef.current?.wharf?.clearAllHighlight?.(); }} /> : null}
            {measureOpen ? <FindMeasurePanel lines={measureLines} unit={measureUnit} onUnit={unit => { setMeasureUnit(unit); mapRef.current?.drawLine?.setUnit?.(unit); }} onDelete={id => { mapRef.current?.drawLine?.deleteLine?.(id); setMeasureLines(lines => lines.filter(line => line.id !== id)); }} onClose={toggleMeasure} /> : null}
            <FindShipToolPanel green={showGreenLayer} greenDisabled={currentZoom >= SHIP_GREEN_DOT_MAX_ZOOM || trackEntries.length > 0} satellite={layerType === "satellite"} measure={measureOpen} harbor={showHarbors} onMeasure={toggleMeasure} onGreen={() => setShowGreenLayer(value => !value)} onHarbor={() => void toggleHarbors()} onSatellite={() => setLayerType(value => value === "satellite" ? "vector" : "satellite")} />
            <Modal className="vehicle-query-limit-dialog" width={400} open={showLimitDialog} footer={null} onCancel={() => setShowLimitDialog(false)} centered maskClosable={false}>
                <div className="vehicle-query-limit-content"><img src="https://static.zhihaoscm.cn/customer-admin/search-limit-dialog-img-2025-11-10.svg" alt="" /><h3>今日查船次数已用完</h3><p>您当前为<strong>{shipUsage?.memberLevelName?.includes("非会员") ? "普通用户" : shipUsage?.memberLevelName}</strong>，每日可查船舶<b>{shipUsage?.totalTimes ?? 0}</b>次，今日次数已用完。开通或升级会员可获得更多每日查船次数。</p><Button type="primary" onClick={() => window.open(`${window.location.origin}/console/me/member`)}>开通/升级会员</Button><button type="button" onClick={() => setShowLimitDialog(false)}>稍后再说</button></div>
            </Modal>
        </section>
    );
}

interface TrackValues {
    vclN: string;
    vco: string;
    range: [dayjs.Dayjs, dayjs.Dayjs];
}
/** 查车页覆盖车牌查询、车辆档案、实时位置、历史记录与轨迹。 */
export function TruckLocationPage() {
    const pageRef = useRef<HTMLElement>(null);
    const mapRef = useRef<ZhihaoMapInstance | undefined>(undefined);
    const initialMapState = useRef(readLegacyMapState("zhihao-web-map-truck", 14));
    const [plate, setPlate] = useState("");
    const [selectedPlate, setSelectedPlate] = useState("");
    const [licenses, setLicenses] = useState<QueryLicenseResponse[]>([]);
    const [positions, setPositions] = useState<Record<string, VehicleInfoResponse | undefined>>({});
    const [history, setHistory] = useState<VehicleHistoryVo[]>([]);
    const [loading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showTrajectory, setShowTrajectory] = useState(false);
    const [trajectoryMini, setTrajectoryMini] = useState(false);
    const [trajectoryEntries, setTrajectoryEntries] = useState<TruckTrajectoryEntry[]>([]);
    const [activeTrajectoryKey, setActiveTrajectoryKey] = useState("");
    const [visibleTrajectoryKeys, setVisibleTrajectoryKeys] = useState<Record<string, boolean>>({});
    const [vehicleUsage, setVehicleUsage] = useState<VehicleQueryLimitInfo | null>(null);
    const [showVehicleUsage, setShowVehicleUsage] = useState(false);
    const [showVehicleLimit, setShowVehicleLimit] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [fallbackFullscreen, setFallbackFullscreen] = useState(false);
    useEffect(() => {
        void vehicleService.getHistoryList().then(setHistory);
        if (isChainCloudTenant()) void shipService.findLimitNum({ type: 2 }).then(setVehicleUsage);
        const persist = () => saveLegacyMapState("zhihao-web-map-truck", mapRef.current);
        window.addEventListener("beforeunload", persist);
        return () => { window.removeEventListener("beforeunload", persist); persist(); };
    }, []);
    useEffect(() => {
        const syncFullscreen = () => setFullscreen(document.fullscreenElement === pageRef.current || fallbackFullscreen);
        document.addEventListener("fullscreenchange", syncFullscreen);
        return () => document.removeEventListener("fullscreenchange", syncFullscreen);
    }, [fallbackFullscreen]);
    /** Vue searchRow.vue 使用同一车牌规则，并在提交前统一转成大写。 */
    const search = async (searchPlate = plate) => {
        const normalizedPlate = searchPlate.trim().toUpperCase();
        if (!normalizedPlate) return;
        const platePattern = /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领A-Z][A-Z][A-Z0-9]{5,6}$/;
        if (!platePattern.test(normalizedPlate)) {
            message.error("您输入的车牌号有误");
            return;
        }
        setLoading(true);
        try {
            if (isChainCloudTenant()) {
                const usage = await shipService.findLimitNum({ type: 2 });
                setVehicleUsage(usage);
                if (usage.overFlag === 1 || (usage.hasUnLimit !== 1 && usage.remainingTimes <= 0)) {
                    setShowVehicleLimit(true);
                    return;
                }
            }
            const nextLicenses = await vehicleService.getLicenseInfo(normalizedPlate, { silentError: true });
            // Vue 的搜索输入和详情车牌相互独立：查询成功后清空输入，详情仍保留本次车牌。
            setSelectedPlate(normalizedPlate);
            setPlate("");
            setLicenses(nextLicenses);
            setPositions({});
            setShowHistory(false);
            if (nextLicenses[0]) {
                const nextHistory = { vclN: normalizedPlate, vclColor: nextLicenses[0].vclColor, ldTn: nextLicenses[0].ldTn, vclTpNm: nextLicenses[0].vclTpNm };
                void vehicleService.addHistory(nextHistory);
                setHistory(items => [nextHistory as VehicleHistoryVo, ...items.filter(item => item.vclN !== normalizedPlate)]);
            }
            if (isChainCloudTenant()) setVehicleUsage(await shipService.findLimitNum({ type: 2 }));
        } catch (error: unknown) {
            // 后端用 30170001 表示查车次数已耗尽，旧页会切换到专用会员引导弹窗。
            if (String((error as { code?: unknown })?.code) === "30170001") {
                if (isChainCloudTenant()) setVehicleUsage(await shipService.findLimitNum({ type: 2 }));
                setShowVehicleLimit(true);
                return;
            }
            throw error;
        } finally {
            setLoading(false);
        }
    };
    const queryTrack = async (entry: TruckTrajectoryEntry, values: TrackValues) => {
        const response = await vehicleService.getRouterPath({
                vclN: values.vclN,
                vco: values.vco,
                qryBtm: values.range[0].format("YYYY-MM-DD HH:mm:ss"),
                qryEtm: values.range[1].format("YYYY-MM-DD HH:mm:ss")
            });
        if (!response.trackArray?.length) {
            mapRef.current?.carTrack?.renderTrack(values.vclN, [], "#1C5BFF");
            setVisibleTrajectoryKeys(items => ({ ...items, [entry.key]: false }));
            message.error("找不到轨迹数据，请查询其他时间范围");
            return;
        }
        const points = response.trackArray.map((point: TrackInfo) => {
            const rawLongitude = Number(point.lon);
            const rawLatitude = Number(point.lat);
            const converted = mapRef.current?.utils.convertSixHundredThousandToLatLng?.(rawLongitude, rawLatitude) ?? [rawLongitude, rawLatitude];
            return { lon: converted[0], lat: converted[1], createdAt: dayjs(point.gtm.replace("/", " ")).valueOf(), speed: point.spd, cog: point.agl, state: null };
        });
        mapRef.current?.carTrack?.renderTrack(values.vclN, points, "#1C5BFF");
        mapRef.current?.carTrack?.remove();
        setVisibleTrajectoryKeys(items => ({ ...items, [entry.key]: true }));
    };
    /** Vue 的车辆追踪与轨迹分开：仅点击追踪时请求实时位置。 */
    const locateVehicle = async (archive: QueryLicenseResponse) => {
        const key = `${selectedPlate}_${archive.vclColor}`;
        const nextPosition = await vehicleService.getTransTimeManage(key);
        setPositions(items => ({ ...items, [key]: nextPosition }));
        mapRef.current?.carTrack?.closeTrack();
        mapRef.current?.carTrack?.location(nextPosition?.firstVcl);
    };
    /** Vue 以“车牌 + 车牌颜色”去重累加轨迹，并把新点击车辆设为活动 Tab。 */
    const openVehicleTrajectory = (archive: QueryLicenseResponse) => {
        const key = `${selectedPlate}_${Number(archive.vclColor)}`;
        const entry: TruckTrajectoryEntry = { key, plate: selectedPlate, archive };
        mapRef.current?.carTrack?.remove();
        setTrajectoryEntries(items => items.some(item => item.key === key) ? items : [...items, entry]);
        setActiveTrajectoryKey(key);
        setTrajectoryMini(false);
        setShowTrajectory(true);
    };
    /** 旧页只在多 Tab 时允许删除，删除后选择相邻车辆并重新查询其轨迹。 */
    const removeVehicleTrajectory = (key: string) => {
        setTrajectoryEntries(items => {
            if (items.length <= 1) return items;
            const removedIndex = items.findIndex(item => item.key === key);
            const next = items.filter(item => item.key !== key);
            if (key === activeTrajectoryKey) setActiveTrajectoryKey(next[Math.max(0, removedIndex - 1)]?.key ?? next[0]?.key ?? "");
            return next;
        });
        setVisibleTrajectoryKeys(items => { const next = { ...items }; delete next[key]; return next; });
    };
    const closeVehicleTrajectories = () => {
        mapRef.current?.carTrack?.closeTrack();
        setVisibleTrajectoryKeys({});
        setTrajectoryEntries([]);
        setActiveTrajectoryKey("");
        setShowTrajectory(false);
        setTrajectoryMini(false);
    };
    /** 删除历史后同步本地列表，避免额外通用列表状态。 */
    const deleteVehicleHistory = async (item: VehicleHistoryVo) => {
        await vehicleService.deleteHistory({ vclN: item.vclN, vclColor: item.vclColor, ldTn: item.ldTn, vclTpNm: item.vclTpNm });
        setHistory(items => items.filter(row => row.vclN !== item.vclN));
    };
    /** 使用旧页相同的原生全屏能力和 iconfont 状态。 */
    const switchTruckFullscreen = async () => {
        if (fallbackFullscreen) { setFallbackFullscreen(false); setFullscreen(false); return; }
        if (document.fullscreenElement === pageRef.current) { await document.exitFullscreen?.(); return; }
        try {
            if (!pageRef.current?.requestFullscreen) throw new Error("Fullscreen API unavailable");
            await pageRef.current.requestFullscreen();
            setFullscreen(true);
        } catch {
            setFallbackFullscreen(true);
            setFullscreen(true);
        }
    };
    return (
        <section ref={pageRef} className={`truck-location-page${fallbackFullscreen ? " is-fallback-fullscreen" : ""}`}>
            <ZhihaoMap label="车辆定位" layerType="vector" center={initialMapState.current.center} showCenterMarker={false} zoom={initialMapState.current.zoom} onReady={instance => { mapRef.current = instance; }} />
            <div className="truck-location-search">
                <div className="truck-location-input">
                    <Input
                        value={plate}
                        allowClear
                        onChange={event => { setPlate(event.target.value.toUpperCase()); if (!event.target.value) { setLicenses([]); setShowHistory(true); } }}
                        onPressEnter={() => void search()}
                        onFocus={() => { if (!plate) { setShowHistory(true); setLicenses([]); } }}
                        placeholder="请输入车牌号搜索"
                    />
                    <Button type="primary" aria-busy={loading} onClick={() => void search()}>
                        <i className="iconfont icon-a-search1" />
                        <span>搜索</span>
                    </Button>
                </div>
                {licenses.length ? <div className="truck-location-detail-list">{licenses.map((archive, index) => {
                    const positionKey = `${selectedPlate}_${archive.vclColor}`;
                    return <TruckSearchDetail key={`${archive.vin || archive.vclColor}-${index}`} plate={selectedPlate} archive={archive} position={positions[positionKey]} onTrack={() => void locateVehicle(archive)} onTrajectory={() => openVehicleTrajectory(archive)} />;
                })}</div> : null}
                {showHistory && !licenses.length ? <TruckSearchHistory history={history} usage={vehicleUsage} showUsage={showVehicleUsage} onClear={() => void vehicleService.clearHistory().then(() => setHistory([]))} onDelete={item => void deleteVehicleHistory(item)} onSelect={value => void search(value)} onToggleUsage={() => setShowVehicleUsage(value => !value)} onUpgrade={() => window.open(`${window.location.origin}/console/me/member`)} /> : null}
            </div>
            {showTrajectory && trajectoryEntries.length && !trajectoryMini ? <TruckTrajectoryPanel entries={trajectoryEntries} activeKey={activeTrajectoryKey} trackVisible={Boolean(visibleTrajectoryKeys[activeTrajectoryKey])} onActive={setActiveTrajectoryKey} onRemove={removeVehicleTrajectory} onClose={closeVehicleTrajectories} onMini={() => { mapRef.current?.carTrack?.closeTrack(); setVisibleTrajectoryKeys(items => ({ ...items, [activeTrajectoryKey]: false })); setTrajectoryMini(true); }} onQuery={(entry, range, _speed) => void queryTrack(entry, { vclN: entry.plate, vco: String(entry.archive.vclColor), range })} onPlay={(entry, speed) => mapRef.current?.carTrack?.playTrack(entry.plate, speed)} onCenter={entry => mapRef.current?.carTrack?.setCenter(entry.plate)} /> : null}
            {showTrajectory && trajectoryMini ? <TruckTrajectoryMini onOpen={() => setTrajectoryMini(false)} onClose={closeVehicleTrajectories} /> : null}
            <button className="truck-location-fullscreen" aria-label={fullscreen ? "退出全屏" : "全屏"} type="button" onClick={() => void switchTruckFullscreen()}><i className={`iconfont ${fullscreen ? "icon-quxiaoquanping" : "icon-quanping1"}`} /></button>
            <Modal className="vehicle-query-limit-dialog" width={400} open={showVehicleLimit} footer={null} onCancel={() => setShowVehicleLimit(false)} centered maskClosable={false}>
                <div className="vehicle-query-limit-content"><img src="https://static.zhihaoscm.cn/customer-admin/search-limit-dialog-img-2025-11-10.svg" alt="" /><h3>今日查车次数已用完</h3><p>您当前为<strong>{vehicleUsage?.memberLevelName?.includes("非会员") ? "普通用户" : vehicleUsage?.memberLevelName}</strong>，每日可查车辆<b>{vehicleUsage?.totalTimes ?? 0}</b>次，今日次数已用完。开通或升级会员可获得更多每日查车次数。</p><Button type="primary" onClick={() => window.open(`${window.location.origin}/console/me/member`)}>开通/升级会员</Button><button type="button" onClick={() => setShowVehicleLimit(false)}>稍后再说</button></div>
            </Modal>
        </section>
    );
}
